import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Directive,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import * as moment from 'moment';
import Swal from 'sweetalert2';

import { ORIGINATOR_ID } from '../../shared/enums/common.enum';
import {
  CCY_SORT_ORDER,
  VALIDATOR_ONLY_SPACES,
} from '../../shared/constants/helpers.const';
import { TradeHttpService } from '../../transactions/services/trade-http.service';
import { SpinnerService } from '../../shared/services/spinner.service';
import { OpenTradeComponentService } from '../open-trade/services/open-trade.component.service';
import { OpenTradeHttpService } from '../common/services/open-trade-http.service';
import { selectCurrencyList } from '../../shared/state/selectors/currency.selector';
import {
  ICredebtor,
  RequiredDocType,
} from '../open-trade/interface/i-credebtor';
import { ITradeDocumentModel } from '../open-trade/interface/i-trade-document.model';
import { extractErrorList, getFormData } from '../../shared/helpers/utils';
import { PercentFormatter } from '../../shared/helpers/percent-formatter.helper';
import { numberValueFormatter } from '../../shared/helpers/number-value-formatter';
import { VALIDATOR_ONLY_SPECIAL_CHARACTERS } from '../../shared/validators/custom-validators.validator';
import { extensionToMimeTypeMap } from '../../shared/extension-to-mime-type.map';

@Directive()
export abstract class OpenTradeBaseComponent implements OnInit {
  protected readonly originatorId = ORIGINATOR_ID;
  protected readonly tradeTransactionsHttpService = inject(TradeHttpService);
  protected readonly store = inject(Store);
  protected readonly spinnerService = inject(SpinnerService);
  protected readonly componentService = inject(OpenTradeComponentService);
  protected readonly fb = inject(FormBuilder);
  protected readonly openTradeHttpService = inject(OpenTradeHttpService);

  // Common properties
  creditorList: ICredebtor[] = [];
  debtorList: ICredebtor[] = [];
  years: { id: string; name: string }[] = [];
  currencyList: string[] = [];
  ccySortOrder = CCY_SORT_ORDER;
  issuedDateMaxDate: Date;
  protected readonly tradeDocControlName = 'documents';
  protected isEdit: boolean;

  // File handling properties (used by B, C, D components)
  readonly allowedFileExtensions = [
    'pdf',
    'doc',
    'docx',
    'csv',
    'xls',
    'xlsx',
    'jpg',
    'jpeg',
    'gif',
    'png',
    'mp3',
    'mp4',
    'video/mp4',
  ];
  readonly fileAccept = this.allowedFileExtensions
    .map((item) => '.' + item)
    .join(',');
  readonly allowedMimeTypes: string[] = this.allowedFileExtensions
    .map((extension: string) => extensionToMimeTypeMap.get(extension) || null)
    .filter(Boolean) as string[];
  readonly selectedCredebtorTradeDocTypes$ = new BehaviorSubject<
    RequiredDocType[]
  >([]);
  uploadedFiles: File | null = null;
  isFileSelected: boolean;
  remainingValue: string;
  totalFaceValue = 0;

  // RSA form properties (used by B, C, D components)
  isShowRsaForm: boolean;
  detectExistRsa = true;

  @Input() type: string;
  @Input() currentData: any;
  @Input() isEditTrade: boolean;
  @Output() isCloseModal = new EventEmitter<boolean>();

  ngOnInit(): void {
    this.getEntityList();
    this.getYears();
    this.generateMaxDate();
    this.getCcyList();
    this.onInitialize();
  }

  /**
   * Hook for child components to implement specific initialization logic
   */
  protected abstract onInitialize(): void;

  /**
   * Get the main form from the child component
   */
  protected abstract getMainForm(): FormGroup;

  /**
   * Get creditor/debtor lists
   */
  protected getEntityList(): void {
    forkJoin({
      creditor: this.tradeTransactionsHttpService.getCreditorsList(),
      debtor: this.tradeTransactionsHttpService.getDebtorsList(),
    }).subscribe(({ creditor, debtor }) => {
      this.creditorList = this.sortByName(creditor);
      this.debtorList = this.sortByName(debtor);
    });
  }

  /**
   * Sort items by name alphabetically
   */
  protected sortByName(items: any[]): any[] {
    return items.sort((a, b) => {
      const nameA = a.name?.trim().toLowerCase() || '';
      const nameB = b.name?.trim().toLowerCase() || '';
      return nameA?.localeCompare(nameB);
    });
  }

  /**
   * Generate list of years from current year to 1950
   */
  protected getYears(): void {
    this.years = [];
    const currentYear = new Date().getFullYear();
    for (let year = currentYear; year >= 1950; year--) {
      this.years.push({ id: year.toString(), name: year.toString() });
    }
  }

  /**
   * Generate max date (1 year from today)
   */
  protected generateMaxDate(): void {
    const today = new Date();
    const nextYear = new Date(today);
    nextYear.setFullYear(today.getFullYear() + 1);
    this.issuedDateMaxDate = nextYear;
  }

  /**
   * Get currency list from store and sort
   */
  protected getCcyList(): void {
    this.store.select(selectCurrencyList).subscribe((currencyList) => {
      this.currencyList = [...currencyList].sort(
        (a, b) => this.ccySortOrder.indexOf(a) - this.ccySortOrder.indexOf(b),
      );
    });
  }

  /**
   * Close modal and emit event
   */
  closeModal(isUpdated?: boolean): void {
    this.isCloseModal.emit(isUpdated);
  }

  /**
   * Custom search filter for ng-select
   */
  customSearch(term: string, item: any): boolean {
    if (!term) {
      return true;
    }
    const searchTerm = term.toLowerCase();
    const nameMatches = item.name.toLowerCase().includes(searchTerm);
    const idMatches = item.id.toString().toLowerCase().includes(searchTerm);
    return nameMatches || idMatches;
  }

  /**
   * Format deposit rate using percent formatter
   */
  formatSellRate(formName: string): void {
    const form = this.getMainForm();
    if (!form) return;

    const formattedValue = PercentFormatter(+form.value.deposit, false);
    form
      .get('deposit')
      ?.patchValue(
        +form.value.deposit > 100 || +form.value.deposit < 0
          ? +form.value.deposit
          : formattedValue,
        {
          emitEvent: false,
        },
      );
  }

  /**
   * Submit form data
   */
  protected submitFormData(formData: any): void {
    this.spinnerService.setShowSpinner(true);

    let formattedRawData = { ...formData };
    formattedRawData.type = formattedRawData.type || this.type;
    formattedRawData.deposit = formData.deposit
      ? Number(formData.deposit) / 100
      : null;
    formattedRawData.faceValue = formData.faceValue
      ? Number(formData.faceValue)
      : null;
    formattedRawData.unitPrice = formData.unitPrice
      ? Number(formData.unitPrice)
      : null;
    formattedRawData.months = formData.months ? Number(formData.months) : null;

    // Handle optional fields (used in B, C, D components)
    if (formattedRawData.edso) {
      formattedRawData.edso = formData.edso ? Number(formData.edso) : null;
    }
    if (formattedRawData.noOfEtr) {
      formattedRawData.noOfEtr = formData.noOfEtr
        ? Number(formData.noOfEtr)
        : null;
    }
    if (formattedRawData.originatorId) {
      formattedRawData.originatorId = this.originatorId;
    }

    this.isEdit
      ? (formattedRawData.description = formattedRawData.rejectedMessage)
      : formattedRawData.description;
    delete formattedRawData.pod;
    delete formattedRawData.rejectedMessage;

    formattedRawData = getFormData(formattedRawData);
    this.currentData?.acquisitionId
      ? this.doUpdate(formattedRawData)
      : this.doCreate(formattedRawData);
  }

  /**
   * Format date to YYYY-MM-DD
   */
  protected formatDate(date: any): string | null {
    if (!date) return null;
    const { year, month, day } = date || {};
    const formattedDate =
      year && month && day
        ? moment(`${year}-${month}-${day}`, 'YYYY-M-D')
        : moment(date);

    return formattedDate.isValid() ? formattedDate.format('YYYY-MM-DD') : date;
  }

  /**
   * Create new trade
   */
  protected doCreate(data: any): void {
    const dateFields = [
      'issuedDate',
      'PODate',
      'expectedDate',
      'invoiceDate',
      'postedDate',
      'tradedDate',
      'settledDate',
      'closedDate',
    ];
    dateFields.forEach((key) => {
      if (data[key]) {
        data[key] = this.formatDate(data[key]);
      }
    });

    this.componentService.saveTrade(data, this.originatorId).subscribe({
      next: (response) => {
        this.spinnerService.setShowSpinner(false);
        this.onCreateSuccess(response, data);
        Swal.fire({
          icon: 'success',
          text: 'Trade successfully submitted',
          showConfirmButton: false,
          timer: 5000,
        });
      },
      error: (error: any) => {
        this.spinnerService.setShowSpinner(false);
        Swal.fire({
          icon: 'error',
          html: this.extractError(error.error.details || error.error.message),
          text: 'Error',
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      },
    });
  }

  /**
   * Hook for child components to handle create success
   */
  protected onCreateSuccess(response: any, data: any): void {
    this.closeModal();
  }

  /**
   * Update existing trade
   */
  protected doUpdate(data: any): void {
    const dateFields = [
      'issuedDate',
      'PODate',
      'expectedDate',
      'invoiceDate',
      'postedDate',
      'tradedDate',
      'settledDate',
      'closedDate',
    ];
    dateFields.forEach((key) => {
      if (data[key]) {
        data[key] = this.formatDate(data[key]);
      }
    });

    this.componentService
      .updateTrade(data, this.currentData['acquisitionId'], this.originatorId)
      .subscribe({
        next: () => {
          this.spinnerService.setShowSpinner(false);
          this.closeModal(true);
          Swal.fire({
            icon: 'success',
            text: 'Trade successfully updated',
            showConfirmButton: false,
            timer: 5000,
          });
        },
        error: () => {
          this.spinnerService.setShowSpinner(false);
        },
      });
  }

  /**
   * Extract error messages from API response
   */
  protected extractError(err: any): string {
    const messages: any = typeof err === 'string' ? err : extractErrorList(err);
    const paragraphs =
      typeof err === 'string'
        ? messages
        : messages.map((item: any) => `<p>${item}</p>`);
    return typeof err === 'string' ? paragraphs : paragraphs.join('\n');
  }

  // Methods specific to B, C, D components (with RSA forms and file handling)

  /**
   * Prevent negative values in number inputs
   */
  preventNegative(event: KeyboardEvent): void {
    if (event.key === '-' || event.key === 'e') {
      if (!this.isShowRsaForm) {
        event.preventDefault();
      }
    }
  }

  /**
   * Clear file input
   */
  clearAttachFileInput(
    formControl: FormControl,
    fileInputEl: HTMLInputElement,
  ): void {
    fileInputEl.files = null;
    fileInputEl.value = '';
    formControl.setValue(null);
    formControl.updateValueAndValidity();
  }

  /**
   * Handle file selection
   */
  fileChange(element: Event, acronym: string, form: FormGroup): void {
    const target = element.target as HTMLInputElement;
    this.uploadedFiles = target.files?.[0] || null;
    const maxSize = 10_000_000; // 10 MB
    const file = target.files?.[0] || null;

    if (!file) {
      this.uploadedFiles = null;
      target.value = '';
      return;
    }

    if (!this.allowedMimeTypes.includes(file.type)) {
      this.uploadedFiles = null;
      target.value = '';
      Swal.fire({
        icon: 'error',
        text: `Validation failed: Mime type ${file.type} is not allowed.`,
        showConfirmButton: true,
        timer: 5000,
      });
      return;
    }

    if (file.size > maxSize) {
      this.uploadedFiles = null;
      target.value = '';
      Swal.fire({
        icon: 'error',
        text: 'Validation failed: File size must be less than 10 MB.',
        showConfirmButton: true,
        timer: 5000,
      });
      return;
    }

    const tradeDocControl = form.get(this.tradeDocControlName) as FormArray;
    const docControl = tradeDocControl.controls.find(
      (cntrl) => cntrl.get('acronym')?.value === acronym,
    );
    docControl?.get('file')?.setValue(file);
  }

  /**
   * Get trade document controls from form
   */
  getTradeDocumentsControl(
    form: FormGroup,
  ): FormGroup<{ fileNo: FormControl; file: FormControl }>[] {
    const tradeDocControl: FormArray | null = form.get(
      this.tradeDocControlName,
    ) as FormArray;
    if (!tradeDocControl) {
      return [];
    }
    return tradeDocControl.controls as FormGroup[];
  }

  /**
   * Show trade documents for editing
   */
  showTradeDocumentForEdit(
    selectedCredebtor: ICredebtor | null,
    form: FormGroup,
    documents?: any,
  ): void {
    if (form.contains(this.tradeDocControlName)) {
      form.removeControl(this.tradeDocControlName);
      form.updateValueAndValidity();
    }

    if (!selectedCredebtor) {
      return;
    }

    const requiredTradeDocTypes =
      selectedCredebtor.requiredTradeDocTypes.filter(
        (docType) => docType.documentType.category === 'trade',
      );

    form.addControl(
      this.tradeDocControlName,
      this.fb.array(
        requiredTradeDocTypes.map((docType) =>
          this.fb.group<Record<keyof ITradeDocumentModel, FormControl>>({
            acronym: this.fb.control(docType.documentType.acronym),
            documentTypeName: this.fb.control(
              docType.documentType.documentTypeName,
            ),
            documentTypeId: this.fb.control(docType.documentTypeId),
            fileNo: this.fb.control(
              documents?.length
                ? documents.find(
                    (doc: any) => doc.documentTypeId === docType.documentTypeId,
                  ).documentName
                : '',
              { validators: [Validators.required, Validators.maxLength(250)] },
            ),
            file: this.fb.control(null, { validators: Validators.required }),
          }),
        ),
      ),
    );

    form.updateValueAndValidity();
    this.selectedCredebtorTradeDocTypes$.next(requiredTradeDocTypes);
  }

  /**
   * Show trade documents for new entry
   */
  showTradeDocumentFor(
    selectedCredebtor: ICredebtor | null,
    form: FormGroup,
  ): void {
    if (form.contains(this.tradeDocControlName)) {
      form.removeControl(this.tradeDocControlName);
      form.updateValueAndValidity();
    }

    if (!selectedCredebtor) {
      return;
    }

    const requiredTradeDocTypes =
      selectedCredebtor.requiredTradeDocTypes.filter(
        (docType) => docType.documentType.category === 'trade',
      );

    form.addControl(
      this.tradeDocControlName,
      this.fb.array(
        requiredTradeDocTypes.map((docType) =>
          this.fb.group<Record<keyof ITradeDocumentModel, FormControl>>({
            acronym: this.fb.control(docType.documentType.acronym),
            documentTypeName: this.fb.control(
              docType.documentType.documentTypeName,
            ),
            documentTypeId: this.fb.control(docType.documentTypeId),
            fileNo: this.fb.control('', {
              validators: [
                Validators.required,
                VALIDATOR_ONLY_SPACES,
                VALIDATOR_ONLY_SPECIAL_CHARACTERS,
                Validators.maxLength(250),
              ],
            }),
            file: this.fb.control(null, { validators: Validators.required }),
          }),
        ),
      ),
    );

    form.updateValueAndValidity();
    this.selectedCredebtorTradeDocTypes$.next(requiredTradeDocTypes);
  }

  /**
   * Calculate remaining value (used in B, C, D components)
   */
  calculateRemainingValue(faceValue: any): void {
    const faceValueNumber = Number(faceValue);
    const remainingValueNumber = Number(this.totalFaceValue) || 0;
    if (!isNaN(faceValueNumber)) {
      const formattedRemainingValue = remainingValueNumber - faceValueNumber;
      this.remainingValue = numberValueFormatter({
        value: formattedRemainingValue,
      });
    }
  }

  /**
   * Subscribe to invoice date changes
   */
  protected subscribeToInvoiceDateChange(form: FormGroup): void {
    form.get('invoiceDate')?.valueChanges.subscribe((invoiceDate) => {
      if (invoiceDate) {
        const jsDate = new Date(invoiceDate);
        jsDate.setDate(jsDate.getDate() + +form.get('edso')?.value);
        form.get('expectedDate')?.patchValue(jsDate, { emitEvent: false });
      }
    });
  }

  /**
   * Subscribe to payment terms (EDSO) changes
   */
  protected subscribeToPaymentTermsChange(form: FormGroup): void {
    form.get('edso')?.valueChanges.subscribe((data) => {
      if (data) {
        const date = form.get('invoiceDate')?.value;
        if (!date) return;
        const jsDate = new Date(date);
        jsDate.setDate(jsDate.getDate() + +data);
        form.get('expectedDate')?.patchValue(jsDate, { emitEvent: false });
      }
    });
  }

  /**
   * Subscribe to expected date changes
   */
  protected subscribeToExpectedDateChange(form: FormGroup): void {
    form.get('expectedDate')?.valueChanges.subscribe((expectedDate) => {
      const invoiceDate = form.get('invoiceDate')?.value;
      if (expectedDate && invoiceDate) {
        const jsExpectedDate = new Date(expectedDate);
        const jsInvoiceDate = new Date(invoiceDate);
        const diff = jsExpectedDate.getTime() - jsInvoiceDate.getTime();
        const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
        form.get('edso')?.patchValue(diffDays, { emitEvent: false });
      }
    });
  }

  /**
   * Detect RSA for creditor (used in B, C components)
   */
  protected detectRSAForCreditor(
    creditorId: string,
    onRSAFound: (data: any) => void,
    onRSANotFound: () => void,
  ): void {
    forkJoin([
      this.openTradeHttpService
        .getEtrRSA({ status: 'active', creditorId })
        .pipe(take(1)),
      this.openTradeHttpService
        .getAcquisitionRSA({ status: 'active', creditorId })
        .pipe(take(1)),
    ]).subscribe({
      next: ([etrRsa, acquisitionRSA]) => {
        const currentRSA = etrRsa?.length
          ? etrRsa[0]
          : acquisitionRSA?.length
            ? acquisitionRSA[0]
            : null;
        if (currentRSA) {
          this.detectExistRsa = false;
          this.isShowRsaForm = false;
          this.componentService.pageTitle.set('Revolving ETR');
          onRSAFound({
            ...currentRSA,
            faceValue: currentRSA.leftFaceValue,
            rsaTradeId: currentRSA.tradeId,
          });
        } else {
          this.detectExistRsa = false;
          this.isShowRsaForm = true;
          this.componentService.pageTitle.set('Open a Trade');
          onRSANotFound();
        }
      },
      error: (error: any) => {
        console.log(error);
      },
    });
  }

  /**
   * Detect RSA for debtor (used in D component)
   */
  protected detectRSAForDebtor(
    debtorId: string,
    onRSAFound: (data: any) => void,
    onRSANotFound: () => void,
  ): void {
    forkJoin([
      this.openTradeHttpService
        .getEtrRSA({ status: 'active', debtorId })
        .pipe(take(1)),
      this.openTradeHttpService
        .getAcquisitionRSA({ status: 'active', debtorId })
        .pipe(take(1)),
    ]).subscribe({
      next: ([etrRsa, acquisitionRSA]) => {
        const currentRSA = etrRsa?.length
          ? etrRsa[0]
          : acquisitionRSA?.length
            ? acquisitionRSA[0]
            : null;
        if (currentRSA) {
          this.detectExistRsa = false;
          this.isShowRsaForm = false;
          this.componentService.pageTitle.set('Revolving ETR');
          onRSAFound({
            ...currentRSA,
            faceValue: currentRSA.leftFaceValue,
            rsaTradeId: currentRSA.tradeId,
          });
        } else {
          this.detectExistRsa = false;
          this.isShowRsaForm = true;
          this.componentService.pageTitle.set('Open a Trade');
          onRSANotFound();
        }
      },
      error: (error: any) => {
        console.log(error);
      },
    });
  }
}
